import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AssetPicker from '../components/AssetPicker';
import HolderSelect from '../components/HolderSelect';
import { useConfirm } from '../components/ConfirmDialog';
import { useAssetMeta, holderFields, tagText } from '../lib/assets';
import '../components/Assets.css';

/**
 * Discovery: the inbox of things seen but not registered (migration 092).
 *
 * Two things fill it. A laptop's daily report lists what is plugged into it
 * - a screen, a dock, a drive - and an office PC IT asks from here scans
 * the office subnet for everything that answers. Each row is one thing,
 * however often it has been seen, with what was read off it: make, model,
 * serial, address, what it seems to be. IT does one of three things to a
 * row:
 *
 *   Accept   a device is made from it, in the category and place chosen
 *            here (the kind pre-selects the category; the place is where
 *            the PC that saw it was), plugged into the laptop that
 *            reported it when that is how it was seen.
 *   Link     it is a device the register already has - the printer entered
 *            by hand last year - which now learns its address and serial.
 *   Ignore   not ours, or not worth a row: a phone on the Wi-Fi, a
 *            visitor's laptop. Ignored stays ignored however often it is
 *            seen again; the Ignored list is where to find it to restore.
 *
 * Since 095 each row also says WHERE the laptop was when it saw the thing
 * (office, home, a client) - a thing seen at home waits here rather than
 * linking - and an ignored row says WHY (personal, not ours, built in, a
 * duplicate). Rows can be ticked and answered together: Ignore, Personal
 * (ignored, and marked as the holder's own so their next laptop does not
 * bring it back), Restore, Accept (each in the category its kind belongs
 * to). The devices that were linked without a click have their own page,
 * Linked automatically, for the one-off clean-up.
 *
 * The server decides who may be here (write on the whole register, like the
 * import) and whether a scan may run (its own switch, and only from a
 * device placed in an office by its last report).
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const when = (t) => (t ? new Date(t).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');
const ago = (t) => {
  if (!t) return 'never';
  const h = (Date.now() - new Date(t).getTime()) / 3600000;
  if (h < 1) return 'in the last hour';
  if (h < 24) return `${Math.round(h)} hour${Math.round(h) === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

const SEEN = { office: ['In the office', 'success'], away: ['Seen at home', 'warning'], client: ['At a client', 'info'], unknown: ['Place not known', 'idle'] };
const REASON = { personal: 'Personal', not_ours: 'Not ours', integrated: 'Built in', duplicate: 'Duplicate' };
const BUS = { usb: 'USB', display: 'display', thunderbolt: 'Thunderbolt', bluetooth: 'Bluetooth', network: 'the network', internal: 'an internal bus', unknown: 'an unknown port' };

const SHARED_KINDS = ['printer', 'switch', 'access_point', 'router', 'firewall', 'storage', 'camera', 'ups', 'display_device'];

const CONFIDENCE = { high: 'Sure', med: 'Fairly sure', low: 'A guess' };
const SCAN_STATE = {
  waiting: ['Waiting for the device', 'info'], running: ['Running on the device', 'info'],
  answered: ['Answered', 'success'], expired: ['Not collected — the device never checked in', 'warning']
};

/** The TTL a reply left with: a hint at what family of software sent it. */
const ttlHint = (ttl) => (ttl > 128 ? 'network kit' : ttl > 64 ? 'Windows' : ttl > 32 ? 'Linux, Apple or embedded' : null);

/**
 * What to call a row, best name first: make and model; what UPnP calls it;
 * its NetBIOS or mDNS name; its hostname; "Unknown <maker> device" from
 * the MAC block alone; its address. Never a blank.
 */
function titleOf(d) {
  const det = d.details || {};
  const made = [d.manufacturer, d.model].filter(Boolean).join(' ');
  if (made) return made;
  const named = det.ssdp?.friendly || det.netbios?.name || det.mdns?.name || d.hostname;
  if (named) return named;
  if (det.vendor && det.vendor !== 'private') return `Unknown ${det.vendor} device`;
  return d.ip_address || 'Unknown device';
}

/** The names a scan found for a host, other than the one used as its title. */
function namesOf(d, title) {
  const det = d.details || {};
  const names = [d.hostname, det.netbios?.name, det.mdns?.name, det.ssdp?.friendly]
    .filter((n) => n && n !== title && n !== d.model);
  return [...new Set(names)];
}

/**
 * The one thing that tells a row apart at a glance, for the folded card:
 * its address (unless that is already the title), else its MAC, else its
 * serial, else a hostname the title does not already say.
 */
function keyOf(d, title) {
  if (d.ip_address && d.ip_address !== title) return d.ip_address;
  if (d.mac_address) return d.mac_address;
  if (d.serial_number) return `serial ${d.serial_number}`;
  return d.hostname && d.hostname !== title ? d.hostname : '';
}

/** Who made it: the make read off it, else the MAC block's owner (never "private"). */
const makerOf = (d) => d.manufacturer || (d.details?.vendor && d.details.vendor !== 'private' ? d.details.vendor : '');

/** 192.168.1.9 before 192.168.1.10: an IPv4 address as one number; null when it is not one. */
const ipNumber = (ip) => {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip || '');
  return m ? ((Number(m[1]) * 256 + Number(m[2])) * 256 + Number(m[3])) * 256 + Number(m[4]) : null;
};

/*
 * The ways the inbox can be ordered. "Newest, MAC blocks together" is how it
 * always was - the server's newest sighting first, with rows that share a
 * MAC block pulled up under one heading - so it stays the default; sorting
 * by MAC keeps the headings too, since the blocks fall together anyway. The
 * choice is remembered per browser; the filters are not.
 */
const SORTS = [
  ['block', 'Newest, MAC blocks together'],
  ['last', 'Last seen, newest first'],
  ['first', 'Discovered, oldest first'],
  ['found', 'Discovered, newest first'],
  ['name', 'Name'],
  ['kind', 'Kind'],
  ['maker', 'Maker'],
  ['ip', 'IP address'],
  ['mac', 'MAC address'],
  ['reporter', 'Seen by'],
  ['sightings', 'Times seen, most first']
];
const SORT_KEY = 'jarvis.discovery.sort';
const readSort = () => {
  try {
    const s = window.localStorage.getItem(SORT_KEY);
    return SORTS.some(([k]) => k === s) ? s : 'block';
  } catch (e) { return 'block'; }
};

const stamp = (t) => (t ? new Date(t).getTime() : 0);
const newest = (a, b) => stamp(b.last_seen_at) - stamp(a.last_seen_at);
/** Text order, blanks last, "Dell 2" before "Dell 10". */
const byText = (f) => (a, b) => {
  const x = f(a) || '';
  const y = f(b) || '';
  if (!x || !y) return (x ? 0 : 1) - (y ? 0 : 1);
  return x.localeCompare(y, undefined, { sensitivity: 'base', numeric: true });
};
/** Number order, blanks last. */
const byNumber = (f) => (a, b) => {
  const x = f(a);
  const y = f(b);
  if (x == null || y == null) return (x == null ? 1 : 0) - (y == null ? 1 : 0);
  return x - y;
};
const compareFor = (sort, kinds) => ({
  block: newest,
  last: newest,
  first: (a, b) => stamp(a.first_seen_at) - stamp(b.first_seen_at),
  found: (a, b) => stamp(b.first_seen_at) - stamp(a.first_seen_at),
  name: byText(titleOf),
  kind: byText((d) => kinds[d.kind] || d.kind),
  maker: byText(makerOf),
  ip: byNumber((d) => ipNumber(d.ip_address)),
  mac: byText((d) => (d.mac_address || '').toLowerCase()),
  reporter: byText((d) => d.reporter_tag),
  sightings: (a, b) => (Number(b.sightings) || 0) - (Number(a.sightings) || 0)
}[sort] || newest);

const BUS_LABEL = { usb: 'USB', display: 'Display', thunderbolt: 'Thunderbolt', bluetooth: 'Bluetooth', network: 'Network', internal: 'Internal', unknown: 'Unknown port' };
const WITHIN = [['1', 'the last 24 hours'], ['7', 'the last 7 days'], ['30', 'the last 30 days']];

const PROBE = {
  waiting: 'queued: waits for the PC to check in', running: 'running on the PC',
  answered: 'answered', expired: 'not collected: the PC never checked in'
};

/**
 * Everything a scan read off a host, laid out as small labelled clues so a
 * row that is only an address still shows what little it has - and what
 * it does not (no MAC: the PC's neighbour table had no entry for it).
 */
function ScanClues({ d }) {
  const det = d.details || {};
  const clues = [];
  const add = (key, label, value) => { if (value) clues.push([key, label, value]); };
  if (det.ports?.length) add('ports', 'Ports', det.ports.join(', '));
  if (!d.mac_address) add('nomac', 'No MAC', 'not in this PC’s neighbour table');
  if (det.snmp) add('snmp', 'SNMP', [det.snmp.descr, det.snmp.name && `sysName ${det.snmp.name}`, det.snmp.location && `at ${det.snmp.location}`].filter(Boolean).join(' · '));
  if (det.http) add('web', 'Web', [det.http.title && `“${det.http.title}”`, det.http.server && `Server ${det.http.server}`, det.http.realm && `realm “${det.http.realm}”`].filter(Boolean).join(' · '));
  if (det.tls) add('tls', 'TLS', [det.tls.cn, det.tls.issuer && det.tls.issuer !== det.tls.cn && `issued by ${det.tls.issuer}`].filter(Boolean).join(' · '));
  if (det.netbios) add('netbios', 'NetBIOS', [det.netbios.name, det.netbios.workgroup && `in ${det.netbios.workgroup}`, det.netbios.server && 'server service'].filter(Boolean).join(' · '));
  if (det.mdns) add('mdns', 'mDNS', [det.mdns.name, det.mdns.model, det.mdns.services?.length && det.mdns.services.join(' ')].filter(Boolean).join(' · '));
  if (det.ssdp) add('upnp', 'UPnP', [det.ssdp.friendly, [det.ssdp.maker, det.ssdp.model, det.ssdp.number].filter(Boolean).join(' '), det.ssdp.serial && `serial ${det.ssdp.serial}`].filter(Boolean).join(' · '));
  if (det.ttl != null) add('ttl', 'TTL', `${det.ttl}${ttlHint(det.ttl) ? ` — ${ttlHint(det.ttl)}` : ''}`);
  if (det.why?.length) add('why', 'Why', det.why.join(' · '));
  if (d.probe_state) add('probe', 'Probe', `${PROBE[d.probe_state] || d.probe_state}, ${ago(d.probe_state === 'answered' ? d.probe_answered_at : d.probe_requested_at)}`);
  if (!clues.length) return null;
  return (
    <div className="as-inbox-clues" data-testid="clues">
      {clues.map(([key, label, value]) => (
        <span key={key} className="as-clue" data-clue={key}><b>{label}</b> {value}</span>
      ))}
    </div>
  );
}

export default function AssetDiscovery() {
  const meta = useAssetMeta();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('new');
  const [kind, setKind] = useState('');
  const [error, setError] = useState('');        // from an action: stays until the next action
  const [loadError, setLoadError] = useState(''); // from loading the inbox
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(null);       // the row being accepted or linked
  const [mode, setMode] = useState('accept');
  const [scanners, setScanners] = useState(null);
  const [reporters, setReporters] = useState(null);
  const [scans, setScans] = useState([]);
  const [scanner, setScanner] = useState('');
  const [reporter, setReporter] = useState('');
  const [every, setEvery] = useState(false);
  const [asking, setAsking] = useState(false);
  // 095: the quick filters, the ticked rows and the bulk bar.
  const [seen, setSeen] = useState('');
  const [bus, setBus] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [olderScans, setOlderScans] = useState(false);
  // The two ways of asking start folded to their picker and button; the
  // words, the "every" buttons and the recent requests wait behind a click.
  const [askOpen, setAskOpen] = useState({ attached: false, scan: false });
  const flipAsk = (k) => setAskOpen((o) => ({ ...o, [k]: !o[k] }));
  // The order (remembered) and the filters the page applies itself, on top
  // of the kind, place and bus the server is asked for.
  const [sort, setSort] = useState(readSort);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [seenBy, setSeenBy] = useState('');
  const [named, setNamed] = useState(false);
  const [within, setWithin] = useState('');
  // Cards open to their detail one by one; folded again on every visit.
  const [expanded, setExpanded] = useState(() => new Set());
  const [confirm, confirmDialog] = useConfirm();
  const chooseSort = (v) => {
    setSort(v);
    try { window.localStorage.setItem(SORT_KEY, v); } catch (e) { /* storage may be blocked */ }
  };

  const load = useCallback(() => {
    const q = new URLSearchParams();
    q.set('status', status);
    if (kind) q.set('kind', kind);
    if (seen) q.set('seen', seen);
    if (bus) q.set('bus', bus);
    axios.get(`${API_BASE}/assets/discoveries?${q}`)
      .then((r) => { setData(r.data); setLoadError(''); })
      .catch((e) => setLoadError(e.response?.data?.error || 'Could not load the inbox.'));
    axios.get(`${API_BASE}/assets/discoveries/scans`).then((r) => setScans(r.data?.scans || [])).catch(() => {});
  }, [status, kind, seen, bus]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPicked(new Set()); }, [status, kind, seen, bus]);
  useEffect(() => {
    axios.get(`${API_BASE}/assets/discoveries/scanners`).then((r) => setScanners(r.data)).catch(() => setScanners(null));
    axios.get(`${API_BASE}/assets/discoveries/reporters`).then((r) => setReporters(r.data)).catch(() => setReporters(null));
  }, []);

  // A scan out: ask again every twenty seconds until it is answered, so the
  // inbox fills while IT watches.
  const outstanding = scans.some((s) => s.state === 'waiting' || s.state === 'running');
  useEffect(() => {
    if (!outstanding) return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [outstanding, load]);

  const act = async (fn, said) => {
    setError('');
    setNote('');
    try {
      const r = await fn();
      if (said) setNote(typeof said === 'function' ? said(r) : said);
      load();
      return r;
    } catch (e) {
      setError(e.response?.data?.error || 'That did not work.');
      return null;
    }
  };

  // Link, with the server's say on a mismatch (a monitor "linked" to a
  // docking station): it answers 409, we ask in its words, and only a yes
  // sends it again with confirm_mismatch. null = the person said no.
  const linkTo = async (d, assetId) => {
    const url = `${API_BASE}/assets/discoveries/${d.id}/link`;
    try {
      return await axios.post(url, { asset_id: assetId });
    } catch (e) {
      if (e.response?.status !== 409 || !e.response.data?.mismatch) throw e;
      const m = e.response.data.mismatch;
      const yes = await confirm({
        title: `Is this ${(m.kind_label || 'device').toLowerCase()} really that device?`,
        message: `${e.response.data.error || ''} Usually you want Accept, then Plugged into.`.trim(),
        confirmLabel: 'Yes, it is the same device', cancelLabel: 'No, go back'
      });
      if (!yes) return null;
      return axios.post(url, { asset_id: assetId, confirm_mismatch: true });
    }
  };

  const undoLink = async (d) => {
    const yes = await confirm({
      title: 'Undo this link?',
      message: `It goes back to the inbox as new; the details it copied onto ${d.asset_tag || 'the device'} are cleared.`,
      confirmLabel: 'Undo link', danger: true
    });
    if (!yes) return;
    act(() => axios.post(`${API_BASE}/assets/discoveries/${d.id}/undo-link`), (r) => {
      const c = r?.data?.cleared || [];
      return c.length
        ? `Link undone. Cleared from ${d.asset_tag || 'the device'}: ${c.map((f) => f.replace(/_/g, ' ')).join(', ')}. It is back in the inbox.`
        : 'Link undone. Nothing had been copied onto the device. It is back in the inbox.';
    });
  };

  // The four ways of asking: one device or every device for what is
  // plugged in; one office PC or every office for what is on the network.
  const ask = (path, body) => act(async () => {
    setAsking(true);
    try {
      return await axios.post(`${API_BASE}/assets/discoveries/${path}`, body);
    } finally { setAsking(false); }
  }, (r) => {
    const d = r?.data || {};
    if (d.asked !== undefined && d.asked + d.skipped > 1) {
      return `Asked ${d.asked} device${d.asked === 1 ? '' : 's'}${d.skipped ? `; ${d.skipped} already had this waiting` : ''}. ${d.note || ''}`;
    }
    return d.note || 'Asked.';
  });

  /**
   * One answer for every ticked row. Personal is confirmed first, because it
   * writes a mark that outlives the row; the others are undone with a click.
   */
  const bulkAct = async (action) => {
    // Only what is ticked AND shown: a row a filter has hidden is not answered unseen.
    const ids = tickedShown.map((d) => d.id);
    if (!ids.length) return;
    if (action === 'personal') {
      const who = [...new Set(tickedShown.map((d) => d.reporter_holder || d.reporter_tag).filter(Boolean))];
      const ok = await confirm({
        title: `Mark ${ids.length} thing${ids.length === 1 ? '' : 's'} as personal?`,
        confirmLabel: 'Mark personal',
        message: `Each goes to the Ignored list as personal, and ${who.length === 1 ? `the holder of ${who[0]}` : 'the holder of the laptop that saw it'} gets it recorded as their personal item (no tag, not in the company list) - so the same thing is not linked as the firm’s when their next laptop reports it. Written with your name.`
      });
      if (!ok) return;
    }
    setBusy(true);
    setError('');
    setNote('');
    try {
      const r = await axios.post(`${API_BASE}/assets/discoveries/bulk`, { ids, action });
      const { done = [], refused = [] } = r.data || {};
      const word = { ignore: 'ignored', personal: 'marked personal', restore: 'back in the inbox', accept: 'added to the register' }[action];
      if (done.length) setNote(`${done.length} ${done.length === 1 ? 'thing' : 'things'} ${word}.`);
      if (refused.length) setError(refused.map((x) => x.reason).join(' · '));
      setPicked(new Set());
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'That did not work.');
    } finally { setBusy(false); }
  };
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (loadError && !data) {
    return (
      <div className="container">
        <p><Link to="/assets">← Assets</Link></p>
        <p className="as-note" data-tone="warning">{loadError}</p>
      </div>
    );
  }

  const rows = data?.discoveries || [];
  // 'other' is the inbox's word for a host that said nothing: "Unknown" to IT.
  const kinds = { ...(data?.kinds || {}), ...(data?.kinds?.other ? { other: 'Unknown' } : {}) };
  const counts = data?.counts || {};
  const noise = data?.noise_ready !== false;
  const pickable = Boolean(data) && (status === 'new' || status === 'ignored');

  // The latest three requests; a waiting or running one stays up however old.
  const live = (x) => x.state === 'waiting' || x.state === 'running';
  const shownScans = olderScans ? scans : scans.filter((x, i) => i < 3 || live(x));
  const hiddenScans = scans.length - shownScans.length;

  // The page's own filters, then the chosen order. A tie keeps the newest
  // sighting first (the sort is stable, and the server sends newest first).
  const needle = q.trim().toLowerCase();
  const cutoff = within ? Date.now() - Number(within) * 86400000 : null;
  const reportersSeen = [...new Set(rows.map((d) => d.reporter_tag).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const shown = rows.filter((d) => {
    if (source && d.source !== source) return false;
    if (seenBy && d.reporter_tag !== seenBy) return false;
    if (named && !d.hostname) return false;
    if (cutoff && stamp(d.last_seen_at) < cutoff) return false;
    if (needle) {
      const det = d.details || {};
      const hay = [titleOf(d), d.hostname, d.ip_address, d.mac_address, d.manufacturer, d.model, d.serial_number,
        det.vendor, det.netbios?.name, det.mdns?.name, det.ssdp?.friendly, d.reporter_tag].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
  const cmp = compareFor(sort, kinds);
  const sorted = [...shown].sort((a, b) => cmp(a, b) || newest(a, b));
  const tickedShown = shown.filter((d) => picked.has(d.id));
  const filtering = Boolean(kind || seen || bus || source || seenBy || named || within || needle);
  const clearFilters = () => {
    setKind(''); setSeen(''); setBus(''); setSource(''); setSeenBy(''); setNamed(false); setWithin(''); setQ('');
  };
  const flipCard = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Rows whose MAC shares its first three bytes sit together, where the
  // first of them would have been. A block of one is no group. Only the
  // default order and the MAC order are grouped; any other order would
  // scatter a block under its own heading.
  const blockOf = (d) => {
    const m = /^([0-9a-f]{2})[:-]([0-9a-f]{2})[:-]([0-9a-f]{2})[:-][0-9a-f]{2}[:-][0-9a-f]{2}[:-][0-9a-f]{2}$/i.exec(d.mac_address || '');
    return m ? `${m[1]}:${m[2]}:${m[3]}`.toLowerCase() : null;
  };
  const grouped = sort === 'block' || sort === 'mac';
  const items = (() => {
    if (!grouped) return sorted.map((d) => ({ row: d }));
    const by = new Map();
    sorted.forEach((d) => { const b = blockOf(d); if (b) by.set(b, [...(by.get(b) || []), d]); });
    const out = [];
    const done = new Set();
    sorted.forEach((d) => {
      const b = blockOf(d);
      const g = b && by.get(b).length > 1 ? by.get(b) : null;
      if (!g) { out.push({ row: d }); return; }
      if (done.has(b)) return;
      done.add(b);
      out.push({ head: b, maker: g.find((x) => x.manufacturer)?.manufacturer || 'Unknown maker', ids: g.map((x) => x.id) });
      g.forEach((x) => out.push({ row: x }));
    });
    return out;
  })();
  const selectAll = (ids) => setPicked((prev) => new Set([...prev, ...ids]));

  return (
    <div className="container">
      <p><Link to="/assets">← Assets</Link></p>
      <div className="page-header">
        <h1 className="page-title">Discovery</h1>
        <p className="page-description">
          Things seen on the network or plugged into a reporting device that the register does not have
          yet. Accept one to make a device of it, link it to a device already entered, or ignore it.
          {' '}<Link to="/assets/discovery/approve" data-testid="approve-link">Approve plugged-in devices</Link> takes
          what laptops report plugged into them, laptop by laptop, to their holder or a desk.
          {' '}<Link to="/assets/discovery/cleanup">Linked automatically</Link> lists the devices a report made
          without anybody clicking, for a look-over.
        </p>
      </div>

      {data && !noise && (
        <p className="as-note" data-tone="warning">
          Migration 095 has not run yet (npm run migrate:095): rows cannot say where a thing was seen or why
          it was ignored, Personal is not offered, and the clean-up page is not available.
        </p>
      )}

      {data && !data.attached_enabled && !data.scan_enabled && (
        <p className="as-note" data-tone="warning">
          Nothing fills this inbox yet: both “List what is plugged into each device” and “Allow a scan of
          the office network” are off in Settings → Assets & devices → Device agent.
        </p>
      )}

      <section className="as-panel" style={{ marginBottom: '1rem' }}>
        <h2>Ask the devices</h2>
        {(askOpen.attached || askOpen.scan) && (
          <p className="as-quiet">
            Every request goes to a device’s agent at its next check-in, within a few minutes if it is on.
            The person at the machine sees a notice naming you. Nothing on any machine is changed.
          </p>
        )}
        <div className="as-ask-grid">
          <div className="as-ask" data-ask="attached" data-open={String(askOpen.attached)}>
            <div className="as-diag-head">
              <h3>What is plugged in</h3>
              {data && !data.attached_enabled && <span className="as-chip" data-tone="warning">Switched off</span>}
              <button type="button" className="btn btn-plain btn-small as-ask-fold" aria-expanded={askOpen.attached}
                data-testid="ask-fold-attached" onClick={() => flipAsk('attached')}>{askOpen.attached ? 'Less' : 'More'}</button>
            </div>
            {askOpen.attached && (
              <p className="as-quiet">
                Screens, docks, printers, drives, keyboards, mice, webcams and headsets, as the machine sees them
                right now. One reporting device, or every one.
              </p>
            )}
            <div className="as-inline-form">
              <div className="form-group">
                <label htmlFor="as-reporter">Device</label>
                <select id="as-reporter" value={reporter} onChange={(e) => setReporter(e.target.value)}>
                  <option value="">Choose a reporting device…</option>
                  {(reporters?.reporters || []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {`${s.asset_tag} · ${[s.manufacturer, s.model].filter(Boolean).join(' ')}${s.holder_name ? ` (${s.holder_name})` : ''}${s.where === 'office' ? ` · ${s.office}` : s.where === 'client' ? ` · at ${s.office}` : s.where === 'away' ? ' · away' : ''} · seen ${ago(s.last_seen_at)}`}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="btn btn-primary" disabled={!reporter || asking || !data?.attached_enabled}
                onClick={() => ask('attached', { asset_id: reporter })}>Ask this device</button>
              {askOpen.attached && (
                <button type="button" className="btn btn-secondary" disabled={asking || !data?.attached_enabled || !(reporters?.reporters || []).length}
                  onClick={() => ask('attached', { all: true })}>
                  {`Ask every device${reporters?.reporters?.length ? ` (${reporters.reporters.length})` : ''}`}
                </button>
              )}
            </div>
            {askOpen.attached && reporters && reporters.reporters.length === 0 && (
              <p className="as-quiet">No device has a live agent yet.</p>
            )}
          </div>
          <div className="as-ask" data-ask="scan" data-open={String(askOpen.scan)}>
            <div className="as-diag-head">
              <h3>What is on the network</h3>
              {data && !data.scan_enabled && <span className="as-chip" data-tone="warning">Switched off</span>}
              <button type="button" className="btn btn-plain btn-small as-ask-fold" aria-expanded={askOpen.scan}
                data-testid="ask-fold-scan" onClick={() => flipAsk('scan')}>{askOpen.scan ? 'Less' : 'More'}</button>
            </div>
            {askOpen.scan && (
              <p className="as-quiet">
                An office PC tries every address on its subnet and reports what answers. Only a device whose last
                report placed it in an office is offered. “Every office” asks one PC per known office network.
              </p>
            )}
            <div className="as-inline-form">
              <div className="form-group">
                <label htmlFor="as-scanner">From</label>
                <select id="as-scanner" value={scanner} onChange={(e) => setScanner(e.target.value)}>
                  <option value="">Choose an office PC…</option>
                  {(scanners?.scanners || []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {`${s.asset_tag} · ${[s.manufacturer, s.model].filter(Boolean).join(' ')}${s.holder_name ? ` (${s.holder_name})` : ''} · ${s.office}${s.subnet ? ` ${s.subnet}` : ''} · seen ${ago(s.last_seen_at)}`}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="btn btn-primary" disabled={!scanner || asking || !data?.scan_enabled}
                onClick={() => ask('scan', { asset_id: scanner })}>Scan from this PC</button>
              {askOpen.scan && (
                <>
                  <button type="button" className="btn btn-secondary" disabled={asking || !data?.scan_enabled || !(scanners?.scanners || []).length}
                    onClick={() => ask('scan', { all: true, every })}>Scan every office</button>
                  <label className="as-check" style={{ alignSelf: 'center' }}>
                    <input type="checkbox" checked={every} onChange={(e) => setEvery(e.target.checked)} />
                    <span>from every office PC, not one per office</span>
                  </label>
                </>
              )}
            </div>
            {askOpen.scan && scanners && scanners.scanners.length === 0 && (
              <p className="as-quiet">No reporting device is in an office right now. Add the office to Known networks in Settings → Assets & devices → Enrolment and wait for a report.</p>
            )}
          </div>
        </div>
        {/* The recent requests belong to both: shown once either is open. */}
        {(askOpen.attached || askOpen.scan) && scans.length > 0 && (
          <>
            <h3 className="as-ask-recent">Recent requests</h3>
            <ul className="as-tree" data-testid="scans">
              {shownScans.map((s) => {
                const [word, tone] = SCAN_STATE[s.state] || [s.state, 'idle'];
                const isScan = s.routine === 'network_scan';
                return (
                  <li key={s.id}>
                    <span className="as-chip" data-tone={tone}>{word}</span>
                    <span>{`${isScan ? 'Network scan' : 'Attached devices'} · ${s.asset_tag}${isScan && s.office ? ` in ${s.office}` : ''}, asked by ${s.asked_by || 'IT'} ${ago(s.requested_at)}`}</span>
                    {s.state === 'answered' && s.run_status === 'ok' && isScan && (
                      <span className="as-quiet">{` · ${s.answered ?? '?'} of ${s.scanned ?? '?'} addresses answered${s.subnet ? ` on ${s.subnet}` : ''}`}</span>
                    )}
                    {s.state === 'answered' && s.run_status === 'ok' && !isScan && (
                      <span className="as-quiet">{` · ${s.attached ?? 0} thing${s.attached === 1 ? '' : 's'} plugged in`}</span>
                    )}
                    {s.state === 'answered' && s.run_status !== 'ok' && (
                      <span className="as-quiet">{` · ${s.run_status}: ${s.run_error || (s.run_status === 'unsupported' ? 'this agent is older than 0.4.0' : 'no detail')}`}</span>
                    )}
                  </li>
                );
              })}
            </ul>
            {hiddenScans > 0 && (
              <button type="button" className="btn btn-plain btn-small" data-testid="scans-older" onClick={() => setOlderScans(true)}>{`Show ${hiddenScans} older`}</button>
            )}
            {olderScans && scans.length > 3 && (
              <button type="button" className="btn btn-plain btn-small" data-testid="scans-fewer" onClick={() => setOlderScans(false)}>Show fewer</button>
            )}
          </>
        )}
      </section>

      {loadError && <p className="as-error" role="alert">{loadError}</p>}
      {error && <p className="as-error" role="alert">{error}</p>}
      {note && <p className="as-note" data-tone="info">{note}</p>}

      <div className="stg">
        <div className="stg-row" role="tablist" aria-label="Inbox">
          {[['new', 'Waiting', counts.total], ['accepted', 'Accepted'], ['ignored', 'Ignored']].map(([s, label, n]) => (
            <button key={s} type="button" role="tab" className="stg-tab" data-on={String(status === s)}
              aria-selected={status === s} onClick={() => setStatus(s)}>
              {label}{n > 0 && <span className="stg-count">{n}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="as-toolbar as-inbox-tools">
        <input type="search" value={q} aria-label="Search the inbox" placeholder="Name, hostname, IP, MAC, maker or serial"
          onChange={(e) => setQ(e.target.value)} />
        <select value={sort} aria-label="Sort by" data-testid="inbox-sort" onChange={(e) => chooseSort(e.target.value)}>
          {SORTS.map(([k, label]) => <option key={k} value={k}>{`Sort: ${label}`}</option>)}
        </select>
        <select value={kind} aria-label="Kind" onChange={(e) => setKind(e.target.value)}>
          <option value="">Every kind</option>
          {Object.entries(kinds).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </div>
      <div className="as-toolbar as-inbox-tools">
        <select value={source} aria-label="Found by" onChange={(e) => setSource(e.target.value)}>
          <option value="">Scans and plugged in</option>
          <option value="scan">Found by a network scan</option>
          <option value="attached">Plugged into a device</option>
        </select>
        {noise && (
          <select value={seen} aria-label="Seen where" onChange={(e) => setSeen(e.target.value)}>
            <option value="">Seen anywhere</option>
            {Object.entries(SEEN).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
          </select>
        )}
        <select value={bus} aria-label="Connected by" onChange={(e) => setBus(e.target.value)}>
          <option value="">Any connection</option>
          {Object.entries(BUS_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select value={seenBy} aria-label="Seen by" onChange={(e) => setSeenBy(e.target.value)}>
          <option value="">Seen by any device</option>
          {reportersSeen.map((t) => <option key={t} value={t}>{`Seen by ${t}`}</option>)}
        </select>
        <select value={within} aria-label="Last seen within" onChange={(e) => setWithin(e.target.value)}>
          <option value="">Seen any time</option>
          {WITHIN.map(([k, label]) => <option key={k} value={k}>{`Seen in ${label}`}</option>)}
        </select>
        <label className="as-check">
          <input type="checkbox" checked={named} data-testid="filter-named" onChange={(e) => setNamed(e.target.checked)} />
          <span>Has a hostname</span>
        </label>
      </div>
      <div className="as-filter-chips" role="group" aria-label="Quick filters">
        {noise && (
          <button type="button" className="stg-tab" data-on={String(seen === 'away')} data-filter="seen-away"
            onClick={() => setSeen(seen === 'away' ? '' : 'away')}>Seen at home</button>
        )}
        <button type="button" className="stg-tab" data-on={String(bus === 'bluetooth')} data-filter="bus-bluetooth"
          onClick={() => setBus(bus === 'bluetooth' ? '' : 'bluetooth')}>Bluetooth</button>
        <button type="button" className="stg-tab" data-on={String(kind === 'usb_storage')} data-filter="kind-storage"
          onClick={() => setKind(kind === 'usb_storage' ? '' : 'usb_storage')}>USB storage</button>
        <button type="button" className="stg-tab" data-on={String(kind === 'other')} data-filter="kind-other"
          title="Hosts the scan could not name: an address, perhaps a MAC, and little else"
          onClick={() => setKind(kind === 'other' ? '' : 'other')}>Unknown only</button>
      </div>
      <div className="as-inbox-count">
        <span className="as-quiet" data-testid="inbox-count">{`${shown.length} of ${rows.length} shown`}</span>
        {status === 'new' && counts.total > 0 && (
          <span className="as-quiet">{`${counts.scan} from scans · ${counts.attached} plugged into devices`}</span>
        )}
        <span className="as-spacer" />
        <button type="button" className="btn btn-plain btn-small" disabled={!filtering} onClick={clearFilters}>Clear filters</button>
        <button type="button" className="btn btn-plain btn-small" disabled={!shown.length}
          onClick={() => setExpanded(new Set(shown.map((d) => d.id)))}>Expand all</button>
        <button type="button" className="btn btn-plain btn-small" disabled={!expanded.size}
          onClick={() => setExpanded(new Set())}>Collapse all</button>
      </div>

      {pickable && tickedShown.length > 0 && (
        <div className="as-bulk" data-testid="bulk-inbox">
          <strong>{`${tickedShown.length} ticked`}</strong>
          {status === 'new' && (
            <>
              <button type="button" className="btn btn-primary btn-small" disabled={busy} onClick={() => bulkAct('accept')}
                title="Each in the category its kind belongs to; shared where the kind usually is">Accept</button>
              <button type="button" className="btn btn-secondary btn-small" disabled={busy} onClick={() => bulkAct('ignore')}>Ignore</button>
              {noise && <button type="button" className="btn btn-secondary btn-small" disabled={busy} onClick={() => bulkAct('personal')}>Personal</button>}
            </>
          )}
          {status === 'ignored' && (
            <button type="button" className="btn btn-primary btn-small" disabled={busy} onClick={() => bulkAct('restore')}>Restore</button>
          )}
          <span className="as-spacer" />
          <button type="button" className="btn btn-plain btn-small" onClick={() => setPicked(new Set())}>Untick all</button>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="empty-state"><p>{status === 'new' ? 'Nothing waiting.' : `Nothing ${status}.`}</p></div>
      ) : shown.length === 0 ? (
        <div className="empty-state"><p>Nothing matches these filters.</p></div>
      ) : (
        <ul className="as-inbox">
          {items.map((it) => it.head ? (
            <li key={`block-${it.head}`} className="as-inbox-group" data-block={it.head}>
              <strong>{it.maker}</strong>
              <span className="as-quiet">{`${it.head} · ${it.ids.length} rows`}</span>
              {pickable && (
                <button type="button" className="btn btn-plain btn-small" onClick={() => selectAll(it.ids)}>{`Select all ${it.ids.length}`}</button>
              )}
            </li>
          ) : ((d) => {
            // The card: one line that says what it is and how to tell it
            // apart, the answers under it, and the rest behind "More".
            const title = titleOf(d);
            const key = keyOf(d, title);
            const more = expanded.has(d.id);
            return (
              <li key={d.id} className="as-inbox-row" data-kind={d.kind} data-pickable={String(pickable)} data-id={d.id}
                data-expanded={String(more)} data-open={String(open?.id === d.id)}>
                {pickable && (
                  <div className="as-inbox-pick">
                    <input type="checkbox" className="as-select" checked={picked.has(d.id)} onChange={() => toggle(d.id)}
                      aria-label={`Choose ${[d.manufacturer, d.model].filter(Boolean).join(' ') || d.hostname || d.ip_address || 'this row'}`} />
                  </div>
                )}
                <div className="as-inbox-main">
                  <span className="as-chip" data-tone={d.kind === 'phone' || d.kind === 'other' ? 'idle' : 'info'}
                    data-confidence={d.details?.confidence || undefined}
                    title={d.details?.confidence ? `${CONFIDENCE[d.details.confidence] || ''}${d.details?.why?.length ? `: ${d.details.why.join(' · ')}` : ''}` : undefined}>
                    {kinds[d.kind] || d.kind}
                  </span>
                  {noise && d.source === 'attached' && d.seen_where && d.seen_where !== 'office' && (
                    <span className="as-chip" data-tone={(SEEN[d.seen_where] || SEEN.unknown)[1]} data-seen={d.seen_where}>{(SEEN[d.seen_where] || SEEN.unknown)[0]}</span>
                  )}
                  {d.details?.bus === 'bluetooth' && <span className="as-chip" data-tone="idle">Bluetooth</span>}
                  {d.status === 'ignored' && d.reason && <span className="as-chip" data-tone="idle" data-reason={d.reason}>{REASON[d.reason] || d.reason}</span>}
                  <strong>{title}</strong>
                  {key && <span className="as-inbox-key" data-testid="card-key">{key}</span>}
                </div>
                {d.status === 'new' && d.suggested_link?.asset_id && (
                  <div className="as-inbox-suggest" data-testid="suggested-link">
                    <span className="as-quiet">{`Looks like ${d.suggested_link.asset_tag} (${d.suggested_link.reason || 'same name'}: ${d.suggested_link.hostname || title})`}</span>
                    <button type="button" className="btn btn-secondary btn-small"
                      onClick={() => act(() => linkTo(d, d.suggested_link.asset_id),
                        (r) => (r ? <>Linked to <Link to={`/assets/${d.suggested_link.asset_id}`}>{d.suggested_link.asset_tag}</Link>.</> : ''))}>{`Link to ${d.suggested_link.asset_tag}`}</button>
                  </div>
                )}
                <div className="as-inbox-actions">
                  {d.status === 'new' && (
                    <>
                      <button type="button" className="btn btn-primary btn-small" onClick={() => { setOpen(d); setMode('accept'); }}>Accept</button>
                      <button type="button" className="btn btn-secondary btn-small" title="Same as a device already in the register…" aria-label="Same as a device already in the register"
                        onClick={() => { setOpen(d); setMode('link'); }}>Same as…</button>
                      <button type="button" className="btn btn-plain btn-small"
                        onClick={() => act(() => axios.post(`${API_BASE}/assets/discoveries/${d.id}/ignore`), 'Ignored.')}>Ignore</button>
                      {noise && d.source === 'attached' && (
                        <button type="button" className="btn btn-plain btn-small" title="Ignored, and never linked again for whoever holds the laptop that saw it"
                          onClick={() => act(() => axios.post(`${API_BASE}/assets/discoveries/${d.id}/ignore`, { reason: 'personal' }), 'Marked personal: recorded as its holder’s personal item and not linked as the firm’s again.')}>Personal</button>
                      )}
                      {d.source === 'scan' && (
                        <button type="button" className="btn btn-plain btn-small" disabled={asking || !data?.scan_enabled}
                          title={`${d.reporter_tag || 'The PC that saw this'} scans its subnet again, so this row can fill in`}
                          onClick={() => ask(`${d.id}/probe`, {})}>Probe again</button>
                      )}
                    </>
                  )}
                  {d.status === 'accepted' && d.linked && (
                    <button type="button" className="btn btn-plain btn-small" onClick={() => undoLink(d)}>Undo link</button>
                  )}
                  {d.status === 'ignored' && (
                    <button type="button" className="btn btn-plain btn-small"
                      onClick={() => act(() => axios.post(`${API_BASE}/assets/discoveries/${d.id}/restore`), 'Back in the inbox.')}>Restore</button>
                  )}
                  <button type="button" className="btn btn-plain btn-small as-inbox-more" data-testid="card-more"
                    aria-expanded={more} onClick={() => flipCard(d.id)}>{more ? 'Less' : 'More'}</button>
                </div>
                {more && (
                  <div className="as-inbox-detail" data-testid="card-detail">
                    <div className="as-inbox-ids as-quiet">
                      {d.source === 'scan'
                        ? [d.ip_address !== title && d.ip_address, d.mac_address, d.details?.vendor === 'private' ? 'randomised MAC' : d.details?.vendor,
                          ...namesOf(d, title), d.serial_number && `serial ${d.serial_number}`].filter(Boolean).join(' · ')
                        : [d.hostname && d.hostname !== d.model && d.hostname, d.ip_address, d.mac_address, d.serial_number && `serial ${d.serial_number}`]
                          .filter(Boolean).join(' · ')}
                    </div>
                    <div className="as-inbox-sub as-quiet">
                      {d.source === 'scan'
                        ? `Found on the network by ${d.reporter_tag || 'a scan'}${d.network_name ? ` (${d.network_name})` : ''}`
                        : `Plugged into ${d.reporter_tag || 'a reporting device'}${d.details?.bus ? ` by ${BUS[d.details.bus] || d.details.bus}` : ''}${noise && d.seen_where === 'away' ? ', at home' : noise && d.seen_where === 'client' ? ', at a client' : ''}`}
                      {d.location_path ? ` · ${d.location_path}` : ''}
                      {` · seen ${d.sightings} time${d.sightings === 1 ? '' : 's'}, last ${ago(d.last_seen_at)}`}
                      {d.source === 'scan' && d.sightings > 1 && ` (first ${when(d.first_seen_at)})`}
                      {d.source !== 'scan' && d.details?.ports?.length > 0 && ` · ports ${d.details.ports.join(', ')}`}
                      {d.details?.size_gb != null && ` · ${d.details.size_gb} GB`}
                      {d.status !== 'new' && ` · ${d.status} by ${d.decided_by_name || 'IT'} ${ago(d.decided_at)}`}
                      {d.asset_tag && <> · <Link to={`/assets/${d.asset_id}`}>{d.asset_tag}</Link></>}
                    </div>
                    {d.source === 'scan' && <ScanClues d={d} />}
                  </div>
                )}
                {open?.id === d.id && mode === 'accept' && (
                  <AcceptForm d={d} meta={meta} categoryFor={data?.category_for || {}} onClose={() => setOpen(null)}
                    onDone={(asset) => { setOpen(null); act(async () => null, <>Added as <Link to={`/assets/${asset.id}`}>{tagText(asset)}</Link>.{asset.asset_tag && <>{' '}<Link to={`/assets/labels?ids=${asset.id}`} data-testid="print-its-label">Print its label</Link></>}</>); }} />
                )}
                {open?.id === d.id && mode === 'link' && (
                  <LinkForm d={d} linkTo={linkTo} onClose={() => setOpen(null)}
                    onDone={(asset) => { setOpen(null); act(async () => null, <>Linked to <Link to={`/assets/${asset.id}`}>{tagText(asset)}</Link>.</>); }} />
                )}
              </li>
            );
          })(it.row))}
        </ul>
      )}
      {confirmDialog}
    </div>
  );
}

/** Accept: a device from the row, with the few choices that place it. */
function AcceptForm({ d, meta, categoryFor, onClose, onDone }) {
  const [form, setForm] = useState({
    asset_category_id: categoryFor[d.kind] || '',
    location_id: d.location_id || '',
    holder: '',
    is_shared: SHARED_KINDS.includes(d.kind),
    manufacturer: d.manufacturer || '', model: d.model || '', serial_number: d.serial_number || '', hostname: d.hostname || '',
    asset_tag: ''
  });
  const [parent, setParent] = useState(d.source === 'attached' && d.reporter_asset_id
    ? { id: d.reporter_asset_id, asset_tag: d.reporter_tag, manufacturer: d.reporter_make, model: d.reporter_model } : null);
  const chooseParent = useCallback((a) => setParent(a), []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { holder, ...rest } = form;
      const r = await axios.post(`${API_BASE}/assets/discoveries/${d.id}/accept`, {
        ...rest, location_id: form.location_id || null, ...holderFields(holder),
        parent_asset_id: parent?.id || null, asset_tag: form.asset_tag || null
      });
      onDone(r.data.asset);
    } catch (err) {
      setError({ text: err.response?.data?.error || 'Could not accept it.', field: err.response?.data?.field });
    } finally { setSaving(false); }
  };
  const input = (k, label, props = {}) => (
    <div className="form-group">
      <label htmlFor={`as-acc-${k}`}>{label}</label>
      <input id={`as-acc-${k}`} value={form[k]} onChange={set(k)} aria-invalid={error?.field === k ? 'true' : undefined} {...props} />
    </div>
  );

  return (
    <form className="as-inbox-form" onSubmit={submit}>
      {error && <p className="as-error" role="alert">{error.text}</p>}
      <div className="as-form-grid">
        <div className="form-group">
          <label htmlFor="as-acc-cat">Category</label>
          <select id="as-acc-cat" value={form.asset_category_id} onChange={set('asset_category_id')} required
            aria-invalid={error?.field === 'asset_category_id' ? 'true' : undefined}>
            <option value="">Choose…</option>
            {(meta?.categories || []).filter((c) => c.active !== false).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="as-acc-loc">Kept at</label>
          <select id="as-acc-loc" value={form.location_id} onChange={set('location_id')}>
            <option value="">Not recorded</option>
            {(meta?.locations || []).map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="as-acc-who">With</label>
          <HolderSelect id="as-acc-who" meta={meta} value={form.holder} none="Nobody"
            onChange={(key) => setForm((f) => ({ ...f, holder: key }))} />
        </div>
        <AssetPicker value={parent} onChange={chooseParent} asFormGroup label="Plugged into" />
        {parent && d.source === 'attached' && parent.id === d.reporter_asset_id && (
          <p className="as-quiet" data-testid="plugged-preselected" style={{ gridColumn: '1 / -1', margin: 0 }}>
            {`${parent.asset_tag || 'The laptop'} reported this, so it is chosen as what this is plugged into. Change or clear it if that is wrong.`}
          </p>
        )}
        {input('manufacturer', 'Make')}
        {input('model', 'Model')}
        {input('serial_number', 'Serial number')}
        {input('hostname', 'Hostname')}
        {input('asset_tag', 'Tag (leave empty to number it)')}
        <label className="as-check" style={{ alignSelf: 'end', marginBottom: '0.75rem' }}>
          <input type="checkbox" checked={form.is_shared} onChange={set('is_shared')} />
          <span>Shared equipment (anybody near it may raise a ticket about it)</span>
        </label>
      </div>
      <div className="as-actions" style={{ marginBottom: 0 }}>
        <button type="submit" className="btn btn-primary btn-small" disabled={saving}>Add to the register</button>
        <button type="button" className="btn btn-plain btn-small" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

/** Link: the row IS a device the register has. */
function LinkForm({ d, linkTo, onClose, onDone }) {
  const [asset, setAsset] = useState(null);
  const choose = useCallback((a) => setAsset(a), []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!asset) return;
    setSaving(true);
    setError('');
    try {
      const r = await linkTo(d, asset.id);
      if (r) onDone(r.data.asset);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not link it.');
    } finally { setSaving(false); }
  };
  return (
    <form className="as-inbox-form" onSubmit={submit}>
      {error && <p className="as-error" role="alert">{error}</p>}
      <p className="as-quiet" data-testid="link-explain">Use this when the register already has this exact device - for example a printer typed in by hand. It does not plug it into anything; to record that, use Accept and choose what it is plugged into. The device learns this address, MAC, serial and hostname where it had none, and when it was last seen.</p>
      <AssetPicker value={asset} onChange={choose} asFormGroup label="Which register device is the same as this?" />
      <div className="as-actions" style={{ marginBottom: 0 }}>
        <button type="submit" className="btn btn-primary btn-small" disabled={saving || !asset}>Link them as the same device</button>
        <button type="button" className="btn btn-plain btn-small" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

export { when };
